import { cn } from '@mdvip/shared/lib/utils';
import { useTranslation } from 'react-i18next';

import { filterNutritionFacts, NUTRITION_FACT_LABELS, type NutritionFactInput } from './recipeDetail.util';

export interface RecipeNutritionFactsProps {
  /** The vendor's unfiltered rows. Narrowed internally to `NUTRITION_FACT_LABELS`. */
  facts: NutritionFactInput[];
  /** The licensor's copyright notice, rendered verbatim. */
  attribution: string;
  className?: string;
}

/**
 * Nutrition per serving: the twelve rows the story asks for, two per line as label-and-figure
 * pairs, with no % Daily Value column. Every label reads the same weight — the vendor marks some
 * rows as highlights, which would imply a ranking the page doesn't mean.
 *
 * The attribution rides with this section, so a recipe whose payload matches none of the twelve
 * labels renders neither.
 */
export function RecipeNutritionFacts({ facts, attribution, className }: RecipeNutritionFactsProps) {
  const { t } = useTranslation('shared');
  const rows = filterNutritionFacts(facts, NUTRITION_FACT_LABELS);

  if (rows.length === 0) return null;

  return (
    <section data-slot="recipe-nutrition-facts" className={cn('flex flex-col gap-4', className)}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 className="text-h4 font-semibold text-foreground">{t('recipes.detail.nutrition.title')}</h2>
        <span className="text-sm text-muted-foreground">{t('recipes.detail.nutrition.perServing')}</span>
      </div>
      <dl className="grid grid-cols-1 gap-x-10 @[700px]:grid-cols-2">
        {rows.map((row) => (
          <div key={row.label} className="flex items-baseline justify-between gap-4 border-b border-border py-2.5">
            <dt className="text-sm text-muted-foreground">{row.label}</dt>
            <dd className="text-sm font-medium tabular-nums text-foreground">{row.value}</dd>
          </div>
        ))}
      </dl>
      {attribution && <p className="text-xs leading-relaxed text-muted-foreground">{attribution}</p>}
    </section>
  );
}
